import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import Loader from "src/components/common/Loader";
import Tabs from "src/components/app/Tabs";
import { PanToolAlt } from "@material-symbols-svg/react";

type IncomingDataPoint = {
  currPosition?: string;
  time: string;
  value: number;
};

type ChartPoint = {
  timestamp: number;
  value: number;
  currPosition?: string;
};

type Props = {
  data?: IncomingDataPoint[] | null;
  width?: number | string;
  height?: number;
  className?: string;
  isLive?: boolean;
  isLoading?: boolean;
};

type ViewMode = "24h" | "3h" | "6h" | "12h";

type ColoredSegment = {
  color: string;
  points: ChartPoint[];
};

const VIEW_MODE_MS: Record<ViewMode, number> = {
  "3h": 3 * 60 * 60 * 1000,
  "6h": 6 * 60 * 60 * 1000,
  "12h": 12 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
};
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_CONTINUOUS_GAP_MS = 10 * 60 * 1000;

const LOW_THRESHOLD = 70;
const HIGH_THRESHOLD = 180;

const IN_RANGE_LINE = "#22C55E";
const OUT_RANGE_LINE = "#EF4444";
const GUIDE_LINE = "#9CA3AF";

const FOCUSED_DOT_BORDER = "#3F3F46";
const FOCUSED_DOT_FILL = "#FFFFFF";

const LIVE_DOT_GREEN = "#22C55E";
const LIVE_PULSE_GREEN = "rgba(34, 197, 94, 0.18)";

const isInRange = (value: number) =>
  value >= LOW_THRESHOLD && value <= HIGH_THRESHOLD;

const parseTodayTimeToTimestamp = (timeStr: string): number | null => {
  if (!timeStr) return null;

  const parsed = d3.timeParse("%I:%M %p")(timeStr.trim());
  if (!parsed) return null;

  const now = new Date();
  const finalDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    parsed.getHours(),
    parsed.getMinutes(),
    0,
    0
  );

  return finalDate.getTime();
};

const getCrossingPoint = (
  a: ChartPoint,
  b: ChartPoint,
  threshold: number
): ChartPoint | null => {
  const v1 = a.value;
  const v2 = b.value;

  if (v1 === v2) return null;

  const crosses =
    (v1 < threshold && v2 > threshold) || (v1 > threshold && v2 < threshold);

  if (!crosses) return null;

  const ratio = (threshold - v1) / (v2 - v1);
  const timestamp = a.timestamp + (b.timestamp - a.timestamp) * ratio;

  return {
    timestamp,
    value: threshold,
    currPosition: a.currPosition ?? b.currPosition,
  };
};

const splitDataByTimeGap = (
  data: ChartPoint[],
  maxGapMs: number = MAX_CONTINUOUS_GAP_MS
): ChartPoint[][] => {
  if (data.length === 0) return [];

  const chunks: ChartPoint[][] = [];
  let currentChunk: ChartPoint[] = [data[0]];

  for (let i = 1; i < data.length; i += 1) {
    const prev = data[i - 1];
    const curr = data[i];
    const gap = curr.timestamp - prev.timestamp;

    if (gap > maxGapMs) {
      if (currentChunk.length > 0) chunks.push(currentChunk);
      currentChunk = [curr];
    } else {
      currentChunk.push(curr);
    }
  }

  if (currentChunk.length > 0) chunks.push(currentChunk);

  return chunks;
};

const splitSegmentsByRange = (data: ChartPoint[]): ColoredSegment[] => {
  const chunks = splitDataByTimeGap(data, MAX_CONTINUOUS_GAP_MS);
  const segments: ColoredSegment[] = [];

  chunks.forEach((chunk) => {
    if (chunk.length < 2) return;

    let currentPoints: ChartPoint[] = [chunk[0]];
    let currentColor = isInRange(chunk[0].value)
      ? IN_RANGE_LINE
      : OUT_RANGE_LINE;

    const flushSegment = () => {
      if (currentPoints.length >= 2) {
        segments.push({
          color: currentColor,
          points: [...currentPoints],
        });
      }
    };

    for (let i = 1; i < chunk.length; i += 1) {
      const prev = chunk[i - 1];
      const curr = chunk[i];

      const crossings = [LOW_THRESHOLD, HIGH_THRESHOLD]
        .map((threshold) => getCrossingPoint(prev, curr, threshold))
        .filter((p): p is ChartPoint => !!p)
        .sort((a, b) => a.timestamp - b.timestamp);

      if (crossings.length === 0) {
        const prevIn = isInRange(prev.value);
        const currIn = isInRange(curr.value);

        if (prevIn === currIn) {
          currentPoints.push(curr);
        } else {
          currentPoints.push(curr);
          flushSegment();
          currentColor = currIn ? IN_RANGE_LINE : OUT_RANGE_LINE;
          currentPoints = [prev, curr];
        }
        continue;
      }

      for (const cross of crossings) {
        currentPoints.push(cross);
        flushSegment();
        currentColor = isInRange(curr.value) ? IN_RANGE_LINE : OUT_RANGE_LINE;
        currentPoints = [cross];
      }

      currentPoints.push(curr);
    }

    flushSegment();
  });

  return segments;
};

const DayGlucoseTrendChart: React.FC<Props> = ({
  data,
  width = "100%",
  height = 250,
  className,
  isLive = false,
  isLoading = false,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("24h");
  const [overviewInspectorTimestamp, setOverviewInspectorTimestamp] = useState<
    number | null
  >(null);
  const [showPanHint, setShowPanHint] = useState(false);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ startMs: number }>({ startMs: 0 });
  const hasInitializedViewRef = useRef(false);
  const prevDataKeyRef = useRef<string>("");
  const panHintTimerRef = useRef<number | null>(null);

  const finalData = useMemo<ChartPoint[]>(() => {
    const source = Array.isArray(data) ? data : [];

    return source
      .map((item) => {
        const timestamp = parseTodayTimeToTimestamp(item.time);
        return {
          timestamp: timestamp ?? 0,
          value: item.value,
          currPosition: item.currPosition,
        };
      })
      .filter(
        (d) =>
          Number.isFinite(d.timestamp) &&
          d.timestamp > 0 &&
          Number.isFinite(d.value)
      )
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [data]);

  useEffect(() => {
    const dataKey =
      finalData.length > 0
        ? `${finalData[0]?.timestamp}-${
            finalData[finalData.length - 1]?.timestamp
          }-${finalData.length}`
        : "empty";

    if (prevDataKeyRef.current !== dataKey) {
      hasInitializedViewRef.current = false;
      prevDataKeyRef.current = dataKey;
      setOverviewInspectorTimestamp(null);
    }
  }, [finalData]);

  useEffect(() => {
    hasInitializedViewRef.current = false;
  }, [viewMode]);

  useEffect(() => {
    if (panHintTimerRef.current) {
      window.clearTimeout(panHintTimerRef.current);
      panHintTimerRef.current = null;
    }

    if (viewMode !== "24h") {
      setShowPanHint(true);
      panHintTimerRef.current = window.setTimeout(() => {
        setShowPanHint(false);
      }, 2000);
    } else {
      setShowPanHint(false);
    }

    return () => {
      if (panHintTimerRef.current) {
        window.clearTimeout(panHintTimerRef.current);
        panHintTimerRef.current = null;
      }
    };
  }, [viewMode]);

  useEffect(() => {
    if (!svgRef.current || !chartAreaRef.current || finalData.length === 0) {
      return;
    }

    const containerWidth = chartAreaRef.current.clientWidth || 350;
    const svgWidth =
      typeof width === "number" ? width : Math.max(containerWidth, 80);
    const svgHeight = height;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const margin = { top: 16, right: 24, bottom: 28, left: 48 };
    const innerWidth = Math.max(svgWidth - margin.left - margin.right, 80);
    const innerHeight = Math.max(svgHeight - margin.top - margin.bottom, 120);

    const minTimestamp = finalData[0]?.timestamp ?? Date.now();
    const maxTimestamp =
      finalData[finalData.length - 1]?.timestamp ?? Date.now();

    const latestPoint = finalData[finalData.length - 1];
    const windowMs = VIEW_MODE_MS[viewMode];
    const halfWindowMs = windowMs / 2;

    const dayStartDate = new Date(minTimestamp);
    dayStartDate.setHours(0, 0, 0, 0);
    const dayStartTs = dayStartDate.getTime();
    const dayEndTs = dayStartTs + DAY_MS;

    const minStart =
      viewMode === "24h" ? dayStartTs : minTimestamp - halfWindowMs;
    const maxStart =
      viewMode === "24h" ? dayEndTs - DAY_MS : maxTimestamp - halfWindowMs;

    const initialStart =
      viewMode === "24h" ? dayStartTs : maxTimestamp - halfWindowMs;

    if (!hasInitializedViewRef.current) {
      dragStateRef.current.startMs = initialStart;
      hasInitializedViewRef.current = true;
    }

    const root = svg
      .attr("width", svgWidth)
      .attr("height", svgHeight)
      .attr("viewBox", `0 0 ${svgWidth} ${svgHeight}`);

    const defs = root.append("defs");

    const clipId = `cgm-clip-${Math.random().toString(36).slice(2, 9)}`;
    defs
      .append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", innerWidth)
      .attr("height", innerHeight);

    const focusedShadowId = `focused-shadow-${Math.random()
      .toString(36)
      .slice(2, 9)}`;
    const liveShadowId = `live-shadow-${Math.random()
      .toString(36)
      .slice(2, 9)}`;

    const focusedFilter = defs
      .append("filter")
      .attr("id", focusedShadowId)
      .attr("x", "-200%")
      .attr("y", "-200%")
      .attr("width", "400%")
      .attr("height", "400%");

    focusedFilter
      .append("feDropShadow")
      .attr("dx", 0)
      .attr("dy", 1)
      .attr("stdDeviation", 1.5)
      .attr("flood-color", "#000000")
      .attr("flood-opacity", 0.15);

    const liveFilter = defs
      .append("filter")
      .attr("id", liveShadowId)
      .attr("x", "-200%")
      .attr("y", "-200%")
      .attr("width", "400%")
      .attr("height", "400%");

    liveFilter
      .append("feDropShadow")
      .attr("dx", 0)
      .attr("dy", 1)
      .attr("stdDeviation", 2)
      .attr("flood-color", LIVE_DOT_GREEN)
      .attr("flood-opacity", 0.25);

    const chartRoot = root
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const plotArea = chartRoot.append("g").attr("clip-path", `url(#${clipId})`);
    const backgroundGroup = plotArea.append("g");
    const lineGroup = plotArea.append("g");
    const overlayGroup = chartRoot.append("g");

    const xAxisGroup = chartRoot
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`);

    const yAxisGroup = chartRoot.append("g");

    const centerX = innerWidth / 2;
    const bisect = d3.bisector<ChartPoint, number>((d) => d.timestamp).center;

    const xScale = d3.scaleTime().range([0, innerWidth]);
    const yScale = d3.scaleLinear().range([innerHeight, 0]);

    const bgLow = backgroundGroup
      .append("rect")
      .attr("x", 0)
      .attr("width", innerWidth);
    const bgRange = backgroundGroup
      .append("rect")
      .attr("x", 0)
      .attr("width", innerWidth);
    const bgHigh = backgroundGroup
      .append("rect")
      .attr("x", 0)
      .attr("width", innerWidth);

    const segmentedLineLayer = lineGroup.append("g");

    const centerGuide = overlayGroup
      .append("line")
      .attr("stroke", GUIDE_LINE)
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "6 6");

    const focusedDot = overlayGroup
      .append("circle")
      .attr("r", 4)
      .attr("fill", FOCUSED_DOT_FILL)
      .attr("stroke", FOCUSED_DOT_BORDER)
      .attr("stroke-width", 1.5)
      .attr("filter", `url(#${focusedShadowId})`);

    const liveDotPulse = overlayGroup
      .append("circle")
      .attr("r", 12)
      .attr("fill", LIVE_PULSE_GREEN)
      .style("display", "none");

    const liveDotCoreOuter = overlayGroup
      .append("circle")
      .attr("r", 6)
      .attr("fill", "#FFFFFF")
      .attr("filter", `url(#${liveShadowId})`)
      .style("display", "none");

    const liveDotCoreInner = overlayGroup
      .append("circle")
      .attr("r", 4)
      .attr("fill", LIVE_DOT_GREEN)
      .style("display", "none");

    const mgLabel = chartRoot
      .append("text")
      .attr("x", -22)
      .attr("y", -6)
      .attr("fill", "#111827")
      .style("font-size", "10px")
      .style("font-weight", "600")
      .text("mg/dL");

    const tooltip = tooltipRef.current ? d3.select(tooltipRef.current) : null;

    if (tooltip) {
      tooltip
        .style("position", "absolute")
        .style("pointer-events", "none")
        .style("display", "none")
        .style("padding", "10px 12px")
        .style("border-radius", "14px")
        .style("font-size", "12px")
        .style("line-height", "1.2")
        .style("white-space", "nowrap")
        .style("box-shadow", "0 10px 28px rgba(15, 23, 42, 0.16)")
        .style("z-index", "20");
    }

    const lineGenerator = d3
      .line<ChartPoint>()
      .defined((d) => Number.isFinite(d.value) && Number.isFinite(d.timestamp))
      .x((d) => xScale(d.timestamp))
      .y((d) => yScale(d.value))
      .curve(d3.curveMonotoneX);

    const clampStart = (startMs: number) =>
      Math.min(Math.max(startMs, minStart), maxStart);

    const renderTooltip = (
      focusedPoint: ChartPoint,
      pointX: number,
      pointY: number
    ) => {
      if (!tooltip || !chartAreaRef.current) return;

      const inRange = isInRange(focusedPoint.value);

      tooltip
        .style("display", "block")
        .style("border", `2px solid ${inRange ? "#22C55E" : "#EF4444"}`)
        .style("color", inRange ? "#15803D" : "#DC2626")
        .style("background", inRange ? "#F0FDF4" : "#FEF2F2");

      tooltip.html(
        `<div style="font-weight:700;">${focusedPoint.value} mg/dL</div>
         <div style="font-size:10px;font-weight:500;">${
           focusedPoint.currPosition ?? "-"
         }</div>
         <div style="font-size:10px;margin-top:4px;color:${
           inRange ? "#166534" : "#991B1B"
         };">${d3.timeFormat("%-I:%M %p")(
          new Date(focusedPoint.timestamp)
        )}</div>`
      );

      const chartRect = chartAreaRef.current.getBoundingClientRect();
      const tooltipNode = tooltipRef.current;
      const tooltipWidth = tooltipNode?.offsetWidth ?? 100;
      const tooltipHeight = tooltipNode?.offsetHeight ?? 48;

      const desiredLeft = margin.left + pointX + 14;
      const maxLeft = margin.left + innerWidth - tooltipWidth - 4;
      const minLeft = margin.left + 4;
      const left = Math.max(minLeft, Math.min(desiredLeft, maxLeft));

      const desiredTop = margin.top + pointY - tooltipHeight - 12;
      const fallbackBottomTop = margin.top + pointY + 12;
      const minTop = margin.top + 4;
      const top = desiredTop < minTop ? fallbackBottomTop : desiredTop;

      tooltip.style("left", `${left}px`).style("top", `${top}px`);

      void chartRect;
    };

    const stopLivePulse = () => {
      liveDotPulse.interrupt();
      liveDotPulse.style("display", "none");
      liveDotCoreOuter.style("display", "none");
      liveDotCoreInner.style("display", "none");
    };

    const startLivePulse = () => {
      liveDotPulse.style("display", null);
      liveDotCoreOuter.style("display", null);
      liveDotCoreInner.style("display", null);

      const animate = () => {
        liveDotPulse
          .interrupt()
          .attr("r", 10)
          .attr("fill-opacity", 0.22)
          .transition()
          .duration(1400)
          .ease(d3.easeCubicOut)
          .attr("r", 18)
          .attr("fill-opacity", 0)
          .on("end", animate);
      };

      animate();
    };

    const updateOverviewInspectorFromX = (rawX: number) => {
      if (!Number.isFinite(rawX)) return;

      const boundedX = Math.max(0, Math.min(rawX, innerWidth));
      const tappedTime = xScale.invert(boundedX).getTime();

      if (!Number.isFinite(tappedTime)) return;

      const closestIndex = bisect(finalData, tappedTime);
      const safeIndex = Math.max(
        0,
        Math.min(closestIndex, finalData.length - 1)
      );
      const point = finalData[safeIndex];

      if (point) setOverviewInspectorTimestamp(point.timestamp);
    };

    const render = () => {
      const startMs = clampStart(dragStateRef.current.startMs);
      dragStateRef.current.startMs = startMs;
      const endMs = startMs + windowMs;

      xScale.domain([new Date(startMs), new Date(endMs)]);

      const visibleData = finalData.filter(
        (d) => d.timestamp >= startMs && d.timestamp <= endMs
      );
      const fallbackVisible = visibleData.length > 0 ? visibleData : finalData;

      const visibleMin = d3.min(fallbackVisible, (d) => d.value) ?? 60;
      const visibleMax = d3.max(fallbackVisible, (d) => d.value) ?? 220;

      const yMin = Math.min(0, Math.floor(visibleMin / 10) * 10);
      const yMax = Math.max(250, Math.ceil(visibleMax / 10) * 10);

      yScale.domain([yMin, yMax]).nice();

      const lowTop = yScale(Math.min(LOW_THRESHOLD, yMax));
      const lowBottom = yScale(Math.max(0, yMin));
      bgLow
        .attr("y", lowTop)
        .attr("height", Math.max(lowBottom - lowTop, 0))
        .attr("fill", "#FFFFFF");

      const rangeTop = yScale(Math.min(HIGH_THRESHOLD, yMax));
      const rangeBottom = yScale(Math.max(LOW_THRESHOLD, yMin));
      bgRange
        .attr("y", rangeTop)
        .attr("height", Math.max(rangeBottom - rangeTop, 0))
        .attr("fill", "#22C55E")
        .attr("fill-opacity", 0.12);

      const highTop = yScale(yMax);
      const highBottom = yScale(Math.max(HIGH_THRESHOLD, yMin));
      bgHigh
        .attr("y", highTop)
        .attr("height", Math.max(highBottom - highTop, 0))
        .attr("fill", "#FFFFFF");

      segmentedLineLayer.selectAll("*").remove();

      const lineDataForRender =
        fallbackVisible.length >= 2 ? fallbackVisible : finalData;

      splitSegmentsByRange(lineDataForRender).forEach((segment) => {
        segmentedLineLayer
          .append("path")
          .datum(segment.points)
          .attr("fill", "none")
          .attr("stroke", segment.color)
          .attr("stroke-width", 2.5)
          .attr("stroke-linejoin", "round")
          .attr("stroke-linecap", "round")
          .attr("d", lineGenerator);
      });

      xAxisGroup.call(
        d3
          .axisBottom<Date>(xScale)
          .ticks(viewMode === "24h" ? 6 : 4)
          .tickSizeOuter(0)
          .tickFormat((d) =>
            viewMode === "24h"
              ? d3.timeFormat("%-I %p")(d)
              : d3.timeFormat("%-I:%M %p")(d)
          )
      );

      yAxisGroup.call(d3.axisLeft<number>(yScale).ticks(4).tickSizeOuter(0));

      xAxisGroup
        .selectAll("text")
        .style("font-size", "10px")
        .attr("dy", "0.9em");
      yAxisGroup
        .selectAll("text")
        .style("font-size", "10px")
        .attr("dx", "-2px");

      xAxisGroup
        .selectAll("path,line")
        .attr("stroke", "#1F2937")
        .attr("stroke-width", 1);
      yAxisGroup
        .selectAll("path,line")
        .attr("stroke", "#1F2937")
        .attr("stroke-width", 1);

      const shouldShowInspector =
        viewMode === "3h" ||
        viewMode === "6h" ||
        viewMode === "12h" ||
        overviewInspectorTimestamp !== null;

      if (!shouldShowInspector) {
        centerGuide.style("display", "none");
        focusedDot.style("display", "none");
        tooltip?.style("display", "none");
      } else {
        const inspectorTime =
          viewMode === "24h"
            ? overviewInspectorTimestamp ?? xScale.invert(centerX).getTime()
            : xScale.invert(centerX).getTime();

        const closestIndex = bisect(finalData, inspectorTime);
        const focusedPoint =
          finalData[Math.max(0, Math.min(closestIndex, finalData.length - 1))];

        if (focusedPoint) {
          const pointX =
            viewMode === "24h" ? xScale(focusedPoint.timestamp) : centerX;
          const pointY = yScale(focusedPoint.value);

          centerGuide
            .style("display", null)
            .attr("x1", pointX)
            .attr("x2", pointX)
            .attr("y1", 0)
            .attr("y2", innerHeight);

          focusedDot
            .style("display", null)
            .attr("cx", pointX)
            .attr("cy", pointY);

          renderTooltip(focusedPoint, pointX, pointY);
        } else {
          centerGuide.style("display", "none");
          focusedDot.style("display", "none");
          tooltip?.style("display", "none");
        }
      }

      const latestVisible =
        latestPoint &&
        latestPoint.timestamp >= startMs &&
        latestPoint.timestamp <= endMs;

      if (isLive && latestPoint && (viewMode === "24h" || latestVisible)) {
        const latestX = xScale(latestPoint.timestamp);
        const latestY = yScale(latestPoint.value);

        liveDotPulse.attr("cx", latestX).attr("cy", latestY);
        liveDotCoreOuter.attr("cx", latestX).attr("cy", latestY);
        liveDotCoreInner.attr("cx", latestX).attr("cy", latestY);

        startLivePulse();
      } else {
        stopLivePulse();
      }

      centerGuide.lower();
      focusedDot.raise();
      liveDotPulse.raise();
      liveDotCoreOuter.raise();
      liveDotCoreInner.raise();
      mgLabel.raise();
    };

    render();

    const overlayRect = overlayGroup
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("fill", "transparent");

    if (viewMode === "24h") {
      const getLocalX = (event: any) => {
        const sourceEvent = event?.sourceEvent ?? event;
        const clientX =
          sourceEvent?.touches?.[0]?.clientX ??
          sourceEvent?.changedTouches?.[0]?.clientX ??
          sourceEvent?.clientX;

        if (!Number.isFinite(clientX) || !chartAreaRef.current) return null;

        const rect = chartAreaRef.current.getBoundingClientRect();
        const localX = clientX - rect.left - margin.left;

        return Number.isFinite(localX) ? localX : null;
      };

      overlayRect
        .style("cursor", "pointer")
        .call(
          d3
            .drag<SVGRectElement, unknown>()
            .on("start", (event) => {
              const x = getLocalX(event);
              if (x == null) return;
              updateOverviewInspectorFromX(x);
            })
            .on("drag", (event) => {
              const x = getLocalX(event);
              if (x == null) return;
              updateOverviewInspectorFromX(x);
            })
            .on("end", (event) => {
              const x = getLocalX(event);
              if (x == null) return;
              updateOverviewInspectorFromX(x);
            })
        )
        .on("click", (event) => {
          const x = getLocalX(event);
          if (x == null) return;
          updateOverviewInspectorFromX(x);
        });
    } else {
      overlayRect.style("cursor", "grab").call(
        d3.drag<SVGRectElement, unknown>().on("drag", (event) => {
          const dx = event.dx;
          const msShift = (dx / innerWidth) * windowMs;
          dragStateRef.current.startMs = clampStart(
            dragStateRef.current.startMs - msShift
          );
          render();
        })
      );
    }

    return () => {
      tooltip?.style("display", "none");
      liveDotPulse.interrupt();
    };
  }, [finalData, width, height, isLive, viewMode, overviewInspectorTimestamp]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ width, height }}
      >
        <Loader size="sm" />
      </div>
    );
  }

  if (!data || data.length === 0 || finalData.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-sm text-gray-500 ${
          className ?? ""
        }`}
        style={{ width, height }}
      >
        No data
      </div>
    );
  }

  const tabs = [
    { title: "3h", id: "3h" },
    { title: "6h", id: "6h" },
    { title: "12h", id: "12h" },
    { title: "All Day", id: "24h" },
  ];

  return (
    <div
      ref={wrapperRef}
      className={`z-0 ${className ?? ""}`}
      style={{ width }}
    >
      <div className="p-4">
        <Tabs
          tabs={tabs}
          activeTab={viewMode}
          onChange={(tab) => {
            setViewMode(tab.id as ViewMode);
            setOverviewInspectorTimestamp(null);
          }}
        />
      </div>

      <div
        ref={chartAreaRef}
        style={{ position: "relative", width: "100%", height }}
      >
        {showPanHint && viewMode !== "24h" && (
          <>
            <style>
              {`
                @keyframes cgmPanHintFloat {
                  0% { opacity: 0; transform: translate(-50%, -50%) translateX(-14px); }
                  20% { opacity: 1; transform: translate(-50%, -50%) translateX(-4px); }
                  50% { opacity: 1; transform: translate(-50%, -50%) translateX(8px); }
                  80% { opacity: 1; transform: translate(-50%, -50%) translateX(-2px); }
                  100% { opacity: 0; transform: translate(-50%, -50%) translateX(-14px); }
                }
              `}
            </style>

            <div
              className="pointer-events-none absolute left-1/2 top-1/2 z-20 flex flex-col items-center gap-1"
              style={{
                animation: "cgmPanHintFloat 2s ease-in-out forwards",
              }}
            >
              <div className="rounded-full bg-white/95 p-3 shadow-lg ring-1 ring-black/5">
                <PanToolAlt width={24} height={24} />
              </div>
              <div className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-medium text-gray-700 shadow-sm">
                Drag to scroll
              </div>
            </div>
          </>
        )}

        <svg
          ref={svgRef}
          style={{ display: "block", width: "100%", height: "100%" }}
        />
        <div ref={tooltipRef} />
      </div>
    </div>
  );
};

export default DayGlucoseTrendChart;
